import * as React from 'react';
import { race, take, put } from 'redux-saga/effects';
import { Hooks } from '@grandvisionhq/carts-v2';
import { toast } from '@grandvisionhq/common';
import { Actions } from '@grandvisionhq/configurator';
import { useDispatch, useFeatureToggles, useSelector, useSaga } from '@grandvisionhq/state';
import '../state/actions.js';
import { getAddToCartLineItem } from '../state/add-to-cart/selectors.js';
import '../state/bundles/selectors.js';
import '../state/bundles/reducer.js';
import '../state/configurator/selectors.js';
import '../state/configurator/reducer.js';
import '../state/products/selectors.js';
import '../state/products/reducer.js';
import { addSuccess, addFailed, add } from '../state/add-to-cart/actions.js';

const useConfiguratorStepSubmit = (configuratorNamespace)=>{
    const dispatch = useDispatch();
    const { isPrescriptionRecognitionEnabled  } = useFeatureToggles();
    const lineItemToBeAdded = useSelector(getAddToCartLineItem(isPrescriptionRecognitionEnabled));
    const onAddToCartSuccess = React.useCallback(()=>{
        dispatch(addSuccess());
    }, [
        dispatch
    ]);
    const onValidationError = React.useCallback((validationCodes)=>{
        dispatch(addFailed(new Error(`Add to cart failed with following validation errors: ${validationCodes.join(', ')}`)));
        validationCodes.forEach((code)=>toast.failed(`cart.${code}`)
        );
    }, [
        dispatch
    ]);
    const onError = React.useCallback((errorCode)=>{
        dispatch(addFailed(new Error(`Add to cart failed with following error code: ${errorCode}`)));
        toast.failed(`cart.${errorCode}`);
    }, [
        dispatch
    ]);
    const [addToCart, addToCartResult] = Hooks.useAddToCart();
    // This makes sure the loading state resets when the addToCart call fails
    React.useEffect(()=>{
        if (addToCartResult === null || addToCartResult === void 0 ? void 0 : addToCartResult.error) {
            dispatch(addFailed(addToCartResult.error));
        }
    // Being a bit cautious about showing errors. We only dispatch error when error flips from `undefined` to error`
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        typeof (addToCartResult === null || addToCartResult === void 0 ? void 0 : addToCartResult.error) !== 'undefined'
    ]);
    useSaga(React.useMemo(()=>function*() {
            while(true){
                /**
             * Will catch the end result for both carts-v1 and carts-v2 and signal that to the configurator
             */ const [failure] = yield race([
                    take([
                        addFailed.type
                    ]),
                    take([
                        addSuccess.type
                    ]), 
                ]);
                if (failure) {
                    yield put(Actions.finishFailed({
                        id: configuratorNamespace,
                        reason: new Error(failure.error.message)
                    }));
                    continue;
                }
                yield put(Actions.finishSuccess({
                    id: configuratorNamespace
                }));
            }
        }
    , [
        configuratorNamespace
    ]));
    return React.useCallback((event)=>{
        event === null || event === void 0 ? void 0 : event.preventDefault();
        const draftLineItems = lineItemToBeAdded ? [
            lineItemToBeAdded
        ] : [];
        dispatch(add());
        addToCart(draftLineItems, onAddToCartSuccess, onValidationError, onError);
    }, [
        addToCart,
        lineItemToBeAdded,
        dispatch,
        onAddToCartSuccess,
        onValidationError,
        onError
    ]);
};

export { useConfiguratorStepSubmit };
//# sourceMappingURL=use-configurator-step-submit.js.map
